/**
 * Unified Tag Component
 * Consistent tag visualization across the entire app
 * Uses hierarchical color system: category colors with option overrides
 */

import React from 'react';
import { getTagColor, colorIsLight, getTagTooltip } from '@/utils/drill-ui';
import { Button } from '@/components/ui/button';

interface UnifiedTagProps {
  category: string;
  value: string;
  onClick?: () => void;
  isActive?: boolean;
  showCategory?: boolean;
  size?: 'sm' | 'md' | 'lg';
  muted?: boolean;
  /** Optional tooltip; if omitted, derived from value for IR/ER abbreviations */
  tooltip?: string;
}

export function UnifiedTag({ 
  category, 
  value, 
  onClick, 
  isActive = false,
  showCategory = false,
  size = 'md',
  muted = false,
  tooltip: tooltipProp,
}: UnifiedTagProps) {
  const tooltip = tooltipProp ?? getTagTooltip(value);
  const color = getTagColor(category, value);
  const textColor = colorIsLight(color) ? 'text-fg' : 'text-accent-fg';
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5'
  };
  
  const baseClasses = `inline-flex items-center rounded-full font-medium transition-all duration-200 ${sizeClasses[size]}`;
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80' : '';
  const activeClasses = isActive ? 'ring-2 ring-offset-2 ring-offset-bg ring-accent' : '';
  
  const mutedClasses = muted ? 'bg-bg text-muted' : '';
  const bgStyle = muted ? undefined : { backgroundColor: color };
  const mutedTextColor = muted ? '' : textColor;
  
  const tagContent = (
    <span
      className={`${baseClasses} ${mutedClasses} ${mutedTextColor} ${interactiveClasses} ${activeClasses}`}
      style={bgStyle}
      title={!onClick ? tooltip : undefined}
    >
      {showCategory && <span className="opacity-70 mr-1">{category}:</span>}
      {value}
    </span>
  );
  
  if (onClick) {
    return (
      <button 
        onClick={onClick} 
        className="focus:outline-none focus:ring-0"
        type="button"
        title={tooltip}
      >
        {tagContent}
      </button>
    );
  }
  
  return tagContent;
}

/**
 * Tag Group Component
 * Display multiple tags for a single category
 */

interface TagGroupProps {
  category: string;
  values: string | string[];
  onTagClick?: (value: string) => void;
  activeValues?: Set<string>;
  size?: 'sm' | 'md' | 'lg';
}

export function TagGroup({ 
  category, 
  values, 
  onTagClick, 
  activeValues,
  size = 'md' 
}: TagGroupProps) {
  const valueArray = Array.isArray(values) ? values : [values];
  
  if (valueArray.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {valueArray.map((value) => (
        <UnifiedTag
          key={value}
          category={category}
          value={value}
          onClick={onTagClick ? () => onTagClick(value) : undefined}
          isActive={activeValues?.has(value)}
          size={size}
        />
      ))}
    </div>
  );
}

/**
 * Tag Display for Drill Cards
 * Shows all tags for a drill in organized groups
 */

interface DrillTagsDisplayProps {
  tags: { [key: string]: string | string[] };
  onTagClick?: (category: string, value: string) => void;
  maxCategories?: number;
  priorityCategories?: string[];
}

export function DrillTagsDisplay({ 
  tags, 
  onTagClick,
  maxCategories = 5,
  priorityCategories = ['Phase', 'Domain', 'Body Position', 'Biomechanical Intent']
}: DrillTagsDisplayProps) {
  // Sort categories by priority
  const sortedCategories = Object.keys(tags).sort((a, b) => {
    const aIndex = priorityCategories.indexOf(a);
    const bIndex = priorityCategories.indexOf(b);
    
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });
  
  const displayCategories = sortedCategories.slice(0, maxCategories);
  const remainingCount = sortedCategories.length - maxCategories;
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {displayCategories.map((category) => {
        const values = tags[category];
        const valueArray = Array.isArray(values) ? values : [values];
        
        return valueArray.map((value) => (
          <UnifiedTag
            key={`${category}-${value}`}
            category={category}
            value={value}
            onClick={onTagClick ? () => onTagClick(category, value) : undefined}
            size="sm"
          />
        ));
      })}
      
      {remainingCount > 0 && (
        <span className="text-xs text-muted self-center">
          +{remainingCount} more
        </span>
      )}
    </div>
  );
}

